import type { AxiosError } from 'axios';

import type { GuardrailsErrorBody, GuardrailViolation } from './types';
import { isGuardrailOutcome, isRecord, parseAttemptId } from './types';

const GUARDRAIL_STATUSES = new Set([403, 409]);

// `isGuardrailOutcome` is derived from GUARDRAIL_OUTCOMES rather than re-listing the values: an
// unrecognised type fails `isViolationArray` below, which drops the whole `guardrails` body and
// falls the user back to the generic error toast instead of the veto dialog. So a backend that
// gains an outcome this file has not been told about degrades the *entire* dialog, not one banner.
const isViolation = (value: unknown): value is GuardrailViolation =>
  isRecord(value) &&
  typeof value.key === 'string' &&
  isGuardrailOutcome(value.type) &&
  typeof value.message === 'string';

// Requires a non-empty array: an empty `violations` list fails closed to the normal error
// path rather than opening a heading-only dialog (the backend always sends >= 1 violation).
const isViolationArray = (value: unknown): value is GuardrailViolation[] =>
  Array.isArray(value) && value.length > 0 && value.every(isViolation);

/**
 * Extract the guardrails body from an axios error, or `null` when the error is not a
 * guardrails veto (wrong status, no `guardrails` object, or malformed).
 *
 * The input is deliberately `unknown` (via `AxiosError<unknown>`): this runs inside an
 * interceptor that sees every error body the platform can produce, and the runtime guards
 * below are the only thing that should establish the shape. Typing the input as
 * already-valid would make the compiler treat those checks as dead code and let a field
 * drift (e.g. `is_blocked` renamed) render a *blocked* action as an overridable warning.
 * `unknown` in / `GuardrailsErrorBody | null` out keeps the fail direction safe.
 */
export const parseGuardrailsError = (error: AxiosError<unknown>): GuardrailsErrorBody | null => {
  const response = error?.response;
  if (!response || !GUARDRAIL_STATUSES.has(response.status) || !isRecord(response.data)) {
    return null;
  }
  const guardrails: unknown = response.data.guardrails;
  if (!isRecord(guardrails) || typeof guardrails.is_blocked !== 'boolean' || !isViolationArray(guardrails.violations)) {
    return null;
  }
  return {
    is_blocked: guardrails.is_blocked,
    violations: guardrails.violations,
    // Guarded, not trusted: a malformed id is dropped and the body still returned, so the user
    // keeps their override and only the correlation is lost (the backend drops it the same way).
    attempt_id: parseAttemptId(guardrails.attempt_id),
  };
};

/**
 * Errors already surfaced to the user via the guardrails dialog (a Cancel, or a block the
 * user dismissed). Tracked in a WeakSet — no mutation of the error object — so the central
 * error handler can skip the generic error popup for them, mirroring `reportErrorBase`'s
 * existing session-timeout / permission-denied early-returns.
 */
const handledErrors = new WeakSet<object>();

export const markGuardrailsHandled = (error: AxiosError): void => {
  handledErrors.add(error);
};

export const isGuardrailsHandledError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && handledErrors.has(error);
